import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, Link, Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { ArrowLeft, Newspaper, ChevronRight, Globe, Bookmark, Loader2, SlidersHorizontal, X, Sparkles, ChevronDown, ChevronUp, TrendingUp, Search, Headphones, Pause, Play, Activity, RefreshCcw, Eye, EyeOff, Bell, BellRing, Inbox, CheckCheck, Users, Cpu, Languages } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import { supabase } from '../../lib/supabaseClient'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/EmptyState'
import PullToRefresh from '../../components/PullToRefresh'
import RecentMatchesWidget from '../../components/RecentMatchesWidget'
import { useNewsArticles, useNewsSources, useSavedArticleIds, useReadArticleIds, useTodayDigest, useTodayTrending, useMutedSources, useRedditSearch } from '../../hooks/useNews'
import NewsSubscriptionsSheet from './components/NewsSubscriptionsSheet'
import { useSubscriptionArticles, useSubscriptionUnreadCount } from '../../hooks/useNewsSubscriptions'
import { useReadOnScroll } from '../../hooks/useReadOnScroll'
import RedditCard from './components/RedditCard'
import { NewsArticlePage } from './NewsArticlePage'
import { useTextToSpeech } from '../../hooks/useTextToSpeech'
import { useTextTranslation } from '../../hooks/useTextTranslation'
import { stripMarkdownForTts } from '../../lib/stripMarkdown'
import { performRefresh } from '../../lib/refresh'
import { cn } from '../../lib/utils'

// Persist where the user was so opening an article and tapping back
// doesn't dump them at the top of the list. The app's scrolling
// container isn't the document — it's the <div class="flex-1
// overflow-auto"> in App.jsx — so we walk up parents to find it
// instead of touching window.scrollY.
const SCROLL_STORAGE_KEY = 'phoenix:news-list-scroll'

function findScrollableAncestor(el) {
  let p = el?.parentElement
  while (p && p !== document.body) {
    const s = window.getComputedStyle(p)
    // Only check the overflow setting — don't require scrollHeight >
    // clientHeight, since the page is empty on first mount and that
    // gate would return null before any content has rendered. The
    // save effect needs the ancestor reference set up immediately.
    if (s.overflowY === 'auto' || s.overflowY === 'scroll') {
      return p
    }
    p = p.parentElement
  }
  return null
}

const CATEGORIES = [
  { key: 'all',     value: null },
  { key: 'world',   value: 'world' },
  { key: 'ai',      value: 'ai' },
  { key: 'tech',    value: 'tech' },
  { key: 'science', value: 'science' },
  { key: 'sports',  value: 'sports' },
  { key: 'rights',  value: 'rights' },
  { key: 'local',   value: 'local' },
  { key: 'business',value: 'business' },
]

// Language filter chips. The default behaviour is "match the user's
// UI language" — an Arabic user lands on Arabic articles, an English
// user on English articles — so absence of the URL param encodes that
// default. Picking "All" writes the literal string 'all' to the URL,
// so the explicit cross-language view is still bookmarkable.
const LANGUAGES = [
  { key: 'all', value: 'all' },
  { key: 'ar',  value: 'ar' },
  { key: 'en',  value: 'en' },
]

// Source-kind chips in the filter popover. Mirrors news_sources.kind
// — backed by migration_033, which constrains the column to exactly
// these five values. The "all" entry maps to null so the URL stays
// canonical when nothing's narrowing. Some kinds (currently: reddit)
// only exist as owner_only rows — when RLS filters them out for
// non-owners, the chip auto-disappears since we derive available
// kinds from the visible sources rather than hard-coding the list.
const SOURCE_KINDS = [
  { key: 'all',      value: null },
  { key: 'news',     value: 'news' },
  { key: 'reddit',   value: 'reddit' },
  { key: 'youtube',  value: 'youtube' },
  { key: 'mastodon', value: 'mastodon' },
  { key: 'bluesky',  value: 'bluesky' },
]

function timeAgo(iso, locale) {
  if (!iso) return ''
  const then = Date.parse(iso)
  if (!Number.isFinite(then)) return ''
  const diff = Date.now() - then
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return locale === 'ar' ? 'الآن' : 'now'
  if (mins < 60) return locale === 'ar' ? `${mins}د` : `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return locale === 'ar' ? `${hours}س` : `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 7) return locale === 'ar' ? `${days}ي` : `${days}d`
  // Fall back to a date for anything older than a week.
  return new Date(then).toLocaleDateString(locale === 'ar' ? 'ar-EG' : 'en-CA')
}

// Tag chip vocabulary → emoji + colour cue. The model's allowed to
// return any tag from the prompt's whitelist (see news-enrich); the
// map only styles ones we've seen often. Unknown tags fall through
// to a neutral chip so the UI never breaks on a new vocab term.
const TAG_STYLES = {
  breaking:     { emoji: '🚨', tone: 'text-red-500 bg-red-500/10 border-red-500/30' },
  analysis:     { emoji: '📊', tone: 'text-blue-500 bg-blue-500/10 border-blue-500/30' },
  interview:    { emoji: '🎙', tone: 'text-purple-500 bg-purple-500/10 border-purple-500/30' },
  listicle:     { emoji: '📋', tone: 'text-amber-500 bg-amber-500/10 border-amber-500/30' },
  review:       { emoji: '⭐️', tone: 'text-amber-500 bg-amber-500/10 border-amber-500/30' },
  announcement: { emoji: '📣', tone: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/30' },
  video:        { emoji: '▶️',  tone: 'text-app-primary-500 bg-app-primary-500/10 border-app-primary-500/30' },
  social:       { emoji: '💬', tone: 'text-app-primary-500 bg-app-primary-500/10 border-app-primary-500/30' },
  science:      { emoji: '🔬', tone: 'text-cyan-500 bg-cyan-500/10 border-cyan-500/30' },
  sports:       { emoji: '⚽️', tone: 'text-green-500 bg-green-500/10 border-green-500/30' },
  politics:     { emoji: '🏛',  tone: 'text-rose-500 bg-rose-500/10 border-rose-500/30' },
  finance:      { emoji: '💰', tone: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/30' },
  tech:         { emoji: '💻', tone: 'text-app-primary-500 bg-app-primary-500/10 border-app-primary-500/30' },
}

function TagChip({ tag }) {
  const { t } = useTranslation()
  const style = TAG_STYLES[tag] || { emoji: '', tone: 'text-muted-foreground bg-muted/40 border-border' }
  // Translate when we have a locale entry for this tag; otherwise show
  // the raw tag (defends against future Gemini vocabulary additions
  // before the locale catches up).
  const label = t(`news.tag.${tag}`, { defaultValue: tag })
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded border whitespace-nowrap',
        style.tone,
      )}
      dir="auto"
    >
      {style.emoji && <span aria-hidden="true">{style.emoji}</span>}
      <span>{label}</span>
    </span>
  )
}

// Probe the article title for Arabic-script content. Some sources
// claim language='en' on the row but post mixed AR/EN titles
// (r/saudiarabia, r/Egypt, AJ+ multi-lingual posts). Using the
// actual title characters instead of the source's declared language
// keeps the card's text direction correct per-card.
function articleIsArabic(text) {
  if (!text) return false
  let arabic = 0
  let total = 0
  for (const ch of text) {
    if (/\s/.test(ch)) continue
    total++
    if (/[؀-ۿ]/.test(ch)) arabic++
  }
  if (total === 0) return false
  return arabic / total > 0.3
}

// 1.2K / 4.5M-style compact number for chip + footer labels.
function compactNumber(n) {
  if (!Number.isFinite(n)) return ''
  const abs = Math.abs(n)
  if (abs < 1000) return String(n)
  if (abs < 10000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`
  if (abs < 1000000) return `${Math.round(n / 1000)}K`
  if (abs < 10000000) return `${(n / 1000000).toFixed(1).replace(/\.0$/, '')}M`
  if (abs < 1000000000) return `${Math.round(n / 1000000)}M`
  return `${(n / 1000000000).toFixed(1).replace(/\.0$/, '')}B`
}

// Memoised so unrelated state updates higher up (filter chip taps,
// trending topics arriving, marking another article as read) don't
// re-render every card in the list. The card only renders again
// when one of its own props changes: a different article, a saved /
// read flip on this specific article, locale change, or view swap.
// onToggleRead is identity-stable (see useReadArticleIds → ref'd
// callback) so it doesn't bust the bailout.
const ArticleCard = React.memo(function ArticleCard({ article, locale, isSaved, isRead, onToggleRead, onAutoRead, view, from = 'news', onOpenArticle }) {
  const { t } = useTranslation()
  // Per-card direction from the title's character mix rather than
  // the source's declared language — handles mixed-language sources
  // gracefully.
  const articleDir = articleIsArabic(article.title) ? 'rtl' : 'ltr'
  // Track image load failure so we collapse the slot back rather than
  // showing a broken-icon placeholder. The set lives at the component
  // level so a fail-once stays failed for this mount.
  const [imageFailed, setImageFailed] = useState(false)
  const showImage = !!article.image_url && !imageFailed
  // Auto-mark-as-read on scroll-past with dwell. Wire the outer
  // wrapper ref into the hook; it fires onAutoRead when the card
  // has been ≥ 50% visible for ≥ 1.5s AND has then scrolled past the
  // top edge of the viewport.
  const rootRef = useRef(null)
  useReadOnScroll(rootRef, article.id, onAutoRead, isRead)
  // Memoised URL — `replace()` would otherwise allocate a new string
  // identity on every render, which can cause some browsers to
  // re-decode the image even when the resolved bytes are unchanged.
  // Also keeps the src attribute reference-stable across re-renders.
  const cleanedImageUrl = useMemo(
    () => (article.image_url || '').replace(/&amp;/g, '&'),
    [article.image_url],
  )
  const tags = Array.isArray(article.tags) ? article.tags : []
  const handleToggleRead = (e) => {
    // Don't navigate to the article when the user is using the
    // mark-read button. Stop propagation so the wrapping Link's
    // click handler doesn't fire.
    e.preventDefault()
    e.stopPropagation()
    onToggleRead?.(article.id)
  }
  const handleCardClick = (e) => {
    // Cmd/Ctrl/middle-click or keyboard new-tab: let the browser
    // follow the href to /article/:id (which the cold-load redirect
    // will bounce into a new modal session). Plain click: open the
    // modal in place — no list teardown, no image flash.
    if (e.defaultPrevented) return
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return
    e.preventDefault()
    onOpenArticle?.(article.id)
  }
  return (
    <a
      href={`/article/${article.id}?from=${from}`}
      onClick={handleCardClick}
      className="block"
      ref={rootRef}
      // content-visibility:auto lets the browser skip layout + paint
      // on cards far from the viewport — a list of ~50 cards now only
      // pays the paint cost for the ~3 above-the-fold ones on cold
      // mount. The intrinsic-size hint reserves a sensible height for
      // skipped cards so the scrollbar / scroll-restore math doesn't
      // jump on the first paint.
      style={{ contentVisibility: 'auto', containIntrinsicSize: '0 140px' }}
    >
      <Card
        className={cn(
          'hover:border-app-primary-500/40 transition-colors overflow-hidden',
          // Read view: cards stay dim because "this is your archive"
          // is the entire framing. All view: nothing dims, because
          // already-read articles are filtered out.
          isRead && view === 'read' && 'opacity-60',
        )}
      >
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-start gap-3">
            {showImage && (
              <img
                src={cleanedImageUrl}
                alt=""
                // Lazy now that the list cold-mounts on every back-nav
                // (sibling /article/:id route); decoding 50 thumbnails
                // up-front is the freeze. With content-visibility:auto
                // on the card wrapper above, only the visible cards
                // bring their images into the decode queue at all.
                loading="lazy"
                decoding="async"
                referrerPolicy="no-referrer"
                onError={() => setImageFailed(true)}
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-md object-cover flex-shrink-0 bg-muted"
              />
            )}
            <div className="flex-1 min-w-0">
              <p
                className={cn(
                  'text-sm text-foreground',
                  isRead ? 'font-normal' : 'font-semibold',
                )}
                dir={articleDir}
              >
                {article.title}
              </p>
              {article.summary && (
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2" dir={articleDir}>
                  {article.summary}
                </p>
              )}
              <div className="flex items-center gap-2 mt-2 text-[11px] text-muted-foreground flex-wrap" dir="auto">
                {article.source?.name && (
                  <span className="font-medium">{article.source.name}</span>
                )}
                <span>·</span>
                <span dir="ltr" className="font-mono">{timeAgo(article.published_at, locale)}</span>
                {tags.slice(0, 2).map((tag) => (
                  <TagChip key={tag} tag={tag} />
                ))}
                {/* Mark-as-read / mark-as-unread quick action. Lives
                    inline with the source/time meta so it doesn't
                    eat a whole end-column on cards without images.
                    Stays muted in the meta colour by default;
                    activates the primary tint on hover so it's
                    obviously interactive. */}
                {onToggleRead && (
                  <button
                    type="button"
                    onClick={handleToggleRead}
                    aria-pressed={isRead}
                    aria-label={t(isRead ? 'news.markAsUnread' : 'news.markAsRead')}
                    className={cn(
                      'ms-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] transition-colors',
                      isRead
                        ? 'text-app-primary-500 hover:bg-accent'
                        : 'text-muted-foreground/70 hover:text-app-primary-500 hover:bg-accent',
                    )}
                  >
                    {isRead ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  </button>
                )}
              </div>
            </div>
            {/* Saved indicator — kept as a tiny passive marker at the
                end. The mark-as-read action moved into the meta row
                above. No chevron: the whole card is a link, the
                decoration was redundant. */}
            {isSaved && (
              <Bookmark className="w-4 h-4 text-app-primary-500 fill-current flex-shrink-0 self-center" />
            )}
          </div>
        </CardContent>
      </Card>
    </a>
  )
})

// AI digest card. Renders both daily and weekly digests with the same
// shape — the title is keyed off the digest kind, the markdown body
// expands on tap. Collapsed by default to ~5 lines.
function DigestCard({ digest, locale, t, onRefresh }) {
  const [expanded, setExpanded] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  // The digest row may have come from the other language (fallback in
  // useTodayDigest when the locale's own row isn't ready). When the
  // source differs, we offer a one-tap MS-Translator pass — cheaper
  // than re-generating a second digest from scratch and reuses the
  // same article window. `showTranslated` flips between original and
  // the locally-cached translation.
  const sourceLang = digest.source_language || digest.language || locale
  const translateNeeded = sourceLang !== locale
  const translator = useTextTranslation()
  const { translate } = translator
  const [showTranslated, setShowTranslated] = useState(false)
  // Auto-translate when the source language differs from the locale —
  // saves the user a tap in the common /tech case where the AR row is
  // delayed and we fell back to EN. `translate` is wrapped in useCallback
  // with empty deps inside useTextTranslation so its identity is stable
  // across renders; the `alive` flag prevents a late response from a
  // prior (digest.summary, locale) pair from flipping the wrong state.
  useEffect(() => {
    if (!translateNeeded) return undefined
    let alive = true
    translate(digest.summary, locale).then((out) => {
      if (alive && out) setShowTranslated(true)
    })
    return () => { alive = false }
  }, [digest.summary, locale, translateNeeded, translate])
  const displayedSummary = showTranslated && translator.translation
    ? translator.translation
    : digest.summary
  const displayedLang = showTranslated && translator.translation ? locale : sourceLang
  const titleKey = digest.kind === 'weekly' ? 'news.digest.weeklyTitle' : 'news.digest.title'
  // TTS for the digest body. Local to the card so two open cards
  // (daily + weekly) can speak independently — though we cancel any
  // in-flight utterance when play() is called, so triggering one
  // pauses the other.
  const tts = useTextToSpeech()
  const speakable = stripMarkdownForTts(displayedSummary)
  const handleListen = () => {
    if (!tts.supported || !speakable) return
    if (tts.playing && !tts.paused) tts.pause()
    else if (tts.paused) tts.resume()
    else tts.play(speakable, displayedLang)
  }
  const handleRefresh = async () => {
    if (refreshing || !onRefresh) return
    setRefreshing(true)
    try {
      await onRefresh(digest.kind)
    } finally {
      setRefreshing(false)
    }
  }
  const handleTranslate = async () => {
    if (translator.loading) return
    if (translator.translation) {
      setShowTranslated((v) => !v)
      return
    }
    // Target = opposite of source. Lets the user flip the digest
    // into the other language regardless of which language the UI
    // is currently in (English UI + English digest still gets you
    // an Arabic translation when you tap).
    const target = sourceLang === 'ar' ? 'en' : 'ar'
    const out = await translator.translate(digest.summary, target)
    if (out) setShowTranslated(true)
  }
  return (
    <Card
      className={cn(
        'border-app-primary-500/30 bg-app-primary-500/5 overflow-hidden',
      )}
    >
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-app-primary-500 flex-shrink-0" />
          <p
            className="text-[11px] font-semibold uppercase tracking-wide text-app-primary-500 flex-1 min-w-0"
            dir="auto"
          >
            {t(titleKey)}
          </p>
          {tts.supported && speakable && (
            <button
              type="button"
              onClick={handleListen}
              aria-label={t(
                tts.playing && !tts.paused
                  ? 'news.article.ttsPause'
                  : tts.paused
                    ? 'news.article.ttsResume'
                    : 'news.article.ttsPlay',
              )}
              aria-pressed={tts.playing}
              className={cn(
                'p-1 rounded-md hover:bg-accent transition-colors',
                tts.playing ? 'text-app-primary-500' : 'text-muted-foreground hover:text-app-primary-500',
              )}
            >
              {tts.playing && !tts.paused ? (
                <Pause className="w-3.5 h-3.5" />
              ) : tts.paused ? (
                <Play className="w-3.5 h-3.5" />
              ) : (
                <Headphones className="w-3.5 h-3.5" />
              )}
            </button>
          )}
          {/* Translate toggle — always visible. handleTranslate picks
              the opposite of the source language as the target, so a
              tap flips to the other language regardless of UI locale.
              Uses translate-text (MS Translator) so it doesn't burn
              LLM quota for re-rendering the same content. */}
          <button
            type="button"
            onClick={handleTranslate}
            disabled={translator.loading}
            aria-label={t(
              showTranslated ? 'news.digest.showOriginal' : 'news.digest.translate',
            )}
            title={t(
              showTranslated ? 'news.digest.showOriginal' : 'news.digest.translate',
            )}
            aria-pressed={showTranslated}
            className={cn(
              'p-1 rounded-md hover:bg-accent transition-colors',
              showTranslated ? 'text-app-primary-500' : 'text-muted-foreground hover:text-app-primary-500',
              translator.loading && 'opacity-60',
            )}
          >
            {translator.loading
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Languages className="w-3.5 h-3.5" />}
          </button>
          {/* Manual regenerate. Fires news-digest with force=true for
              this kind, then re-pulls the row from the DB so the UI
              updates without a full page refresh. */}
          {onRefresh && (
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              aria-label={t('news.digest.refresh')}
              title={t('news.digest.refresh')}
              className={cn(
                'p-1 rounded-md hover:bg-accent transition-colors text-muted-foreground hover:text-app-primary-500',
                refreshing && 'opacity-60',
              )}
            >
              <RefreshCcw className={cn('w-3.5 h-3.5', refreshing && 'animate-spin')} />
            </button>
          )}
          <span className="text-[10px] text-muted-foreground font-mono" dir="ltr">
            {digest.article_count} {t('news.digest.articlesShort')}
          </span>
        </div>
        <div
          className={cn(
            'prose prose-sm dark:prose-invert max-w-none text-foreground leading-relaxed',
            // Collapse to ~5 lines via line-clamp until the user opens
            // the full version. Markdown headings keep showing — the
            // clamp just clips the bottom.
            !expanded && 'line-clamp-5',
          )}
          dir={displayedLang === 'ar' ? 'rtl' : 'ltr'}
        >
          <ReactMarkdown>{displayedSummary}</ReactMarkdown>
        </div>
        {translator.error && (
          <p
            className="text-[10px] font-mono text-muted-foreground break-all mt-1"
            dir="ltr"
          >
            {translator.error}
          </p>
        )}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 flex items-center gap-1 text-[11px] text-app-primary-500 hover:underline"
          dir="auto"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-3.5 h-3.5" />
              {t('news.digest.collapse')}
            </>
          ) : (
            <>
              <ChevronDown className="w-3.5 h-3.5" />
              {t('news.digest.expand')}
            </>
          )}
        </button>
      </CardContent>
    </Card>
  )
}

// Trending topics row. Renders the AI-extracted topic labels as
// chips; tapping one filters the article list by that keyword. The
// active topic gets the primary-coloured chip; re-tapping clears.
// Horizontally scrollable like the language/category rows so a long
// list (~12 topics) doesn't wrap and eat vertical space.
function TrendingChips({ topics, activeKeyword, onPick, t }) {
  if (!topics?.length) return null
  return (
    <div className="space-y-1.5">
      <div
        className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground px-1"
        dir="auto"
      >
        <TrendingUp className="w-3 h-3 text-app-primary-500" />
        <span>{t('news.trending.title')}</span>
      </div>
      <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1">
        {topics.map((topic) => {
          const isActive = activeKeyword && topic.keyword === activeKeyword
          return (
            <FilterChip
              key={topic.keyword}
              active={isActive}
              onClick={() => onPick(isActive ? null : topic.keyword)}
            >
              {topic.label}
            </FilterChip>
          )
        })}
      </div>
    </div>
  )
}

// Top-of-feed view filters. Icons (not text) keep the row narrow on
// mobile — the labels are still exposed via aria-label / title for
// accessibility and tooltip hovers on desktop.
const VIEW_CHIPS = [
  { key: 'all',   icon: Inbox },
  { key: 'subs',  icon: BellRing },
  { key: 'read',  icon: CheckCheck },
  { key: 'saved', icon: Bookmark },
]

function FilterChip({ active, onClick, children, ...rest }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      {...rest}
      className={cn(
        'inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs whitespace-nowrap border transition-colors',
        active
          ? 'border-app-primary-500 bg-app-primary-500/10 text-app-primary-500'
          : 'border-input text-muted-foreground hover:bg-accent',
      )}
    >
      {children}
    </button>
  )
}

// `mode` narrows the default article kind for this route. /news
// passes 'news', /social passes 'social'. Inside the page the user
// can still drop into a more specific source kind via the filter
// chips; `mode` only sets the default scope.
export function NewsPage({ mode = 'all' } = {}) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  // location used by several effects below (URL persistence,
  // overlay detection). Declared up top so anything referencing it
  // in the dependency arrays of useEffect / useMemo can resolve at
  // module-init time — was previously declared later, which
  // produced a TDZ error after the news-tab smart routing landed.
  const location = useLocation()
  const goBack = () => navigate('/')
  const locale = i18n.language === 'ar' ? 'ar' : 'en'

  // Article modal state. Reading an article is no longer a route —
  // NewsPage stays mounted at /{mode}, we manually push the URL to
  // /article/:id?from=… via history.pushState so the bar shows the
  // shareable link, and popstate brings the modal back down. The
  // router itself never sees the URL change, so NewsPage doesn't
  // re-render and the card DOM (with its decoded thumbnails) stays
  // alive — which is what kills the back-from-article flash.
  //
  // Seed from location.state.__openArticle so the cold-load redirect
  // (App.jsx's ArticleColdLoadRedirect → /{from}) can hand off an id
  // for the modal to pick up on first paint.
  const [activeArticleId, setActiveArticleId] = useState(
    () => (typeof window !== 'undefined' && window.history.state?.articleModal)
      || location.state?.__openArticle
      || null,
  )

  const openArticle = useCallback((id) => {
    if (!id) return
    setActiveArticleId(id)
    try {
      // Push the same URL with a new state object. Adds a back-stack
      // entry so browser/iOS back collapses the modal, but DOES NOT
      // change the URL bar — guaranteeing react-router can't observe
      // any path change (regardless of Safari's historical quirks
      // around popstate firing on pushState). Standalone PWAs hide
      // the URL bar anyway; we'll add an explicit share affordance
      // later if the article URL needs to be exposed.
      window.history.pushState(
        { articleModal: id },
        '',
        window.location.pathname + window.location.search,
      )
    } catch { /* very old browsers — fall through to in-memory only */ }
  }, [])

  const closeArticle = useCallback(() => {
    if (!activeArticleId) return
    // window.history.back fires popstate, which the listener below
    // turns into the setActiveArticleId(null) call. Going through
    // the back-stack (instead of just setState) keeps the history
    // entry count honest so a second back from the list still pops
    // the user out to the previous tab as expected.
    try { window.history.back() } catch { setActiveArticleId(null) }
  }, [activeArticleId])

  // Sync activeArticleId with the browser back stack. Browser/iOS
  // swipe-back pops the modal entry → popstate fires → we reflect
  // that into state so the modal collapses. The state shape we pushed
  // (`{ articleModal: id }`) supports article-hop history correctly:
  // backing from B's entry leaves us on A's, which restores A in
  // the modal rather than closing it outright.
  useEffect(() => {
    const onPop = (e) => {
      const stateId = e.state?.articleModal
      setActiveArticleId(stateId || null)
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  // Cold-load redirect handoff: if NewsPage mounts with a hint to
  // open an article (notification tap, deep link, refresh on the
  // /article/:id URL), stamp the article id into window.history.state
  // so back-nav still has something to pop, then clear the marker
  // so a remount / HMR doesn't reopen the modal involuntarily.
  // Ref-guarded against double-fire from React Strict Mode / re-runs.
  const handoffDoneRef = useRef(false)
  useEffect(() => {
    if (handoffDoneRef.current) return
    const handoff = location.state?.__openArticle
    if (!handoff) return
    handoffDoneRef.current = true
    try {
      window.history.replaceState(
        { articleModal: handoff },
        '',
        window.location.pathname + window.location.search,
      )
    } catch { /* noop */ }
  }, [location.state])

  // Filters live in the URL so going to /phoenix/news/:id and back
  // keeps the selection. useState here would reset on every remount.
  const [searchParams, setSearchParams] = useSearchParams()
  const category = searchParams.get('cat')
  // Language filter — defaults to the user's UI locale when the URL
  // param is absent (so Arabic users see only Arabic articles by
  // default, English users see only English). Explicit "All" is
  // stored as ?lang=all and surfaces in the query as null (no filter).
  // A specific 'ar' / 'en' override pins that language regardless of
  // the user's UI choice.
  // Default to 'all' now that the article-detail page can translate
  // any post. Previously defaulted to the UI language, which hid
  // half the available content from users who could read both — the
  // translation feature made that filter redundant friction.
  const langParam = searchParams.get('lang')
  const activeLangValue = langParam || 'all'
  const language = activeLangValue === 'all' ? null : activeLangValue
  const sourceKind = searchParams.get('src')
  // Reddit-only sort. 'new' is the default (matches what the rest of
  // the app does); 'top' switches the order clause to upvotes DESC.
  // Anything else (or no value) means "new". Stays in the URL so a
  // refresh / back-nav restores it like every other filter.
  const redditSort = searchParams.get('rsort') === 'top' ? 'top' : null
  const setRedditSort = useCallback((next) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev)
      if (next === 'top') sp.set('rsort', 'top')
      else sp.delete('rsort')
      return sp
    }, { replace: true })
  }, [setSearchParams])
  // Specific source within the selected kind. Optional drill-down
  // — e.g. user picks Source=Reddit, then narrows to r/worldnews only.
  const subId = searchParams.get('subId')
  const topic = searchParams.get('topic')
  // Free-text search query — committed to the URL with a 300ms
  // debounce so each keystroke doesn't fire a Postgres query.
  const search = searchParams.get('q') || ''
  // view is one of 'all' | 'unread' | 'saved'. The default 'all' is
  // omitted from the URL so the canonical /phoenix/news stays clean.
  const view = (() => {
    const v = searchParams.get('view')
    if (v === 'read' || v === 'saved' || v === 'subs') return v
    // Back-compat: the old 'unread' view collapsed into the default
    // 'all' view, which now excludes reads. Old bookmarks keep working.
    if (v === 'unread') return 'all'
    // Back-compat: ?saved=1 → view=saved (kept for existing bookmarks).
    if (searchParams.get('saved') === '1') return 'saved'
    return 'all'
  })()

  const setParam = useCallback((key, value) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (value == null || value === '' || value === false) next.delete(key)
        else next.set(key, value === true ? '1' : value)
        return next
      },
      { replace: true },
    )
  }, [setSearchParams])

  const setCategory = useCallback((v) => setParam('cat', v), [setParam])
  // The default (param absent) is 'all', so picking 'all' just clears
  // the param to keep the URL canonical. Any specific language ('ar' /
  // 'en') is written verbatim — this is what lets an Arabic-locale user
  // actually narrow to Arabic (the old code compared against `locale`
  // and cleared the param, which silently fell back to 'all').
  const setLanguage = useCallback(
    (v) => setParam('lang', v && v !== 'all' ? v : null),
    [setParam],
  )
  // Changing the source kind always clears the more-specific sub-source
  // — the sub belonged to the old kind so it can't compose with the new
  // one. setParam can't do two ops atomically, so set both at once.
  const setSourceKind = useCallback((v) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('subId')
        if (v === null || v === '' || v === false) next.delete('src')
        else next.set('src', v)
        return next
      },
      { replace: true },
    )
  }, [setSearchParams])
  const setSubId = useCallback((v) => setParam('subId', v), [setParam])
  const setTopic = useCallback((v) => setParam('topic', v), [setParam])
  const setSearch = useCallback((v) => setParam('q', v), [setParam])

  // Search uses a local draft that commits to the URL on debounce so
  // each keystroke doesn't trigger a Postgres FTS round-trip.
  const [searchDraft, setSearchDraft] = useState(search)
  useEffect(() => {
    if (searchDraft === search) return undefined
    const timer = setTimeout(() => setSearch(searchDraft.trim() || null), 300)
    return () => clearTimeout(timer)
  }, [searchDraft, search, setSearch])
  // If the URL changes from outside (back navigation, link click)
  // pull the new value back into the draft.
  useEffect(() => { setSearchDraft(search) }, [search])
  const setView = useCallback((v) => {
    // Strip the legacy ?saved=1 when we move to the new param so it
    // doesn't shadow ?view= on subsequent reads.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('saved')
        if (v === 'all') next.delete('view')
        else next.set('view', v)
        return next
      },
      { replace: true },
    )
  }, [setSearchParams])

  const { user } = useAuth()
  const ownerView = hasOwnerAccess(user)
  const sourcesHook = useNewsSources()
  // Derive the kinds we should show in the source chip from the
  // sources RLS hands back. Reddit rows are owner_only — a non-owner
  // sees no reddit_* sources, so the "Reddit" chip disappears. Owners
  // see all kinds they have rows for. We always keep "All" available.
  const visibleSourceKinds = useMemo(() => {
    const kinds = new Set(sourcesHook.sources.map((s) => s.kind).filter(Boolean))
    return SOURCE_KINDS.filter((k) => k.value === null || kinds.has(k.value))
  }, [sourcesHook.sources])
  const mutedHook = useMutedSources()
  const mutedSourceIds = useMemo(() => Array.from(mutedHook.mutedIds), [mutedHook.mutedIds])
  // /tech locks the category filter to 'tech' so the route shows
  // tech articles regardless of which source kind they came from
  // (news, reddit, youtube, …). User's URL ?cat= still wins inside
  // /news + /social — only /tech is hard-locked.
  // KIND_GROUPS uses null for 'tech' (no kind restriction), which
  // lets tech-categorised reddit + youtube articles show too.
  const effectiveCategory = mode === 'tech' ? 'tech' : category
  // Article cards link to /article/${id}?from=${mode} — a top-level
  // sibling route. NewsPage unmounts when the user opens an article
  // and remounts from cache on back-nav, so the list never co-mounts
  // with the article and never has to re-evaluate filters mid-session.
  const articlesHook = useNewsArticles({
    category: effectiveCategory,
    language,
    sourceKind,
    kindGroup: mode === 'all' || mode === 'tech' ? null : mode,
    sourceId: subId,
    topic,
    search,
    mutedSourceIds,
    view,
    redditSort: sourceKind === 'reddit' ? redditSort : null,
  })
  // "From my subs" view — RPC-backed; matches sources + topic
  // keywords across all article ages.
  const subsArticlesHook = useSubscriptionArticles({ unreadOnly: true, limit: 100 })
  const { count: subsUnreadCount } = useSubscriptionUnreadCount()

  // Reddit-only external search. Reuses the same `search` value the
  // local FTS uses, so the user types once and gets the local hits
  // immediately; the "Search Reddit" button below the results pushes
  // out to reddit-search for live results.
  const redditExternal = useRedditSearch()
  useEffect(() => {
    // Drop external results when the search clears or the scope
    // moves away from Reddit.
    if ((!search || sourceKind !== 'reddit') && redditExternal.query) {
      redditExternal.reset()
    }
  }, [search, sourceKind, redditExternal])
  const runRedditExternal = useCallback(() => {
    if (!search) return
    redditExternal.search(search, {
      sort: redditSort === 'top' ? 'top' : 'relevance',
      time: 'month',
    })
  }, [search, redditSort, redditExternal])

  // Sources matching the currently-selected kind. We only show the
  // sub-source picker when there's more than one to choose from —
  // a single-source kind doesn't need a drill-down.
  const subSources = useMemo(() => {
    if (!sourceKind) return []
    return sourcesHook.sources
      .filter((s) => s.kind === sourceKind)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }, [sourcesHook.sources, sourceKind])
  const activeSubName = useMemo(() => {
    if (!subId) return null
    return sourcesHook.sources.find((s) => s.id === subId)?.name || null
  }, [sourcesHook.sources, subId])
  const savedHook = useSavedArticleIds()
  const readHook = useReadArticleIds()

  // Auto-mark-as-read on scroll-past with dwell. Each card runs the
  // useReadOnScroll hook and calls handleAutoRead(id) when the card
  // (a) was visible for ≥ 1.5s, then (b) scrolled past the top
  // edge. Here we batch the resulting marks — every individual scroll
  // would otherwise hit the DB once per article on a fast scroll
  // through twenty cards.
  //
  // Flush every 2.5s while pending, or on unmount. The optimistic
  // local mark fires immediately (markRead via useReadArticleIds)
  // so the dim state appears without waiting for the flush.
  const pendingReadsRef = useRef(new Set())
  const flushTimerRef = useRef(null)
  const flushReads = useCallback(async () => {
    const ids = Array.from(pendingReadsRef.current)
    pendingReadsRef.current.clear()
    flushTimerRef.current = null
    if (!ids.length || !user) return
    // One batch upsert covers every queued id. ignoreDuplicates:true
    // means re-marking a row that's already there is a no-op.
    await supabase
      .from('news_read_articles')
      .upsert(
        ids.map((article_id) => ({ user_id: user.id, article_id })),
        { onConflict: 'user_id,article_id', ignoreDuplicates: true },
      )
  }, [user])
  // Stable-identity callback even as readHook's return object is
  // recreated on every render. Earlier this depended on the whole
  // `readHook` object — its identity changed each render so
  // handleAutoRead changed too, broke React.memo on every ArticleCard,
  // and produced a visible ~1s freeze on back-from-article when the
  // newly-marked-read article triggered a read-set update that flushed
  // a full list re-render. Reading readIds via a ref keeps the deps
  // empty of the mutating Set while still seeing the latest value.
  const readIdsRef = useRef(readHook.readIds)
  readIdsRef.current = readHook.readIds
  const markRead = readHook.markRead
  const handleAutoRead = useCallback((id) => {
    if (!id) return
    if (readIdsRef.current.has(id)) return
    markRead(id)
    pendingReadsRef.current.add(id)
    if (!flushTimerRef.current) {
      flushTimerRef.current = setTimeout(flushReads, 2500)
    }
  }, [markRead, flushReads])
  // Final flush on unmount so reads that piled up just before
  // navigation aren't lost.
  useEffect(() => {
    return () => {
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current)
        flushTimerRef.current = null
      }
      // Best-effort — we can't await in cleanup.
      const ids = Array.from(pendingReadsRef.current)
      pendingReadsRef.current.clear()
      if (ids.length && user) {
        supabase
          .from('news_read_articles')
          .upsert(
            ids.map((article_id) => ({ user_id: user.id, article_id })),
            { onConflict: 'user_id,article_id', ignoreDuplicates: true },
          )
          .then(() => {})
      }
    }
  }, [user])

  // Intentionally NOT filtering already-read articles out of the
  // visible list on the fly. The DB-level filter still runs on
  // first fetch + on pull-to-refresh, so reads disappear when the
  // user explicitly reloads. Between reloads we want the list to
  // stay stable: marking an article as read should write to the
  // DB and flip its eye icon, but should NOT shrink the list (the
  // user feedback was that the mid-session re-render felt
  // disruptive). Cards already-read on first load are never sent
  // here in the first place, so the average user never sees the
  // stale rows anyway.
  // The "subs" view swaps in the RPC-backed subscription articles
  // (matches sources + topic keywords across all ages). Everything
  // else uses the regular paginated list.
  const visibleArticles = view === 'subs' ? subsArticlesHook.articles : articlesHook.articles

  // Chunked rendering — render the first FIRST_CHUNK cards eagerly
  // so the page paints fast on cold mount (the back-from-article path
  // now that the article view is a sibling route), then grow the
  // visible window in idle ticks until the whole hydrated list is in
  // the DOM. Bounds the commit + DOM-creation cost on every mount,
  // which is what the user perceives as the freeze. content-visibility
  // and lazy images handle the once-rendered cards' paint/decode work.
  //
  // Reset whenever the underlying array reference changes — filter
  // change, fetch result, view swap — so the user sees the first chunk
  // of a fresh list immediately rather than catching the tail end of
  // the previous one.
  const FIRST_CHUNK = 8
  const GROW_BATCH = 12
  const [renderedCount, setRenderedCount] = useState(FIRST_CHUNK)
  const lastArticlesArrayRef = useRef(visibleArticles)
  if (lastArticlesArrayRef.current !== visibleArticles) {
    lastArticlesArrayRef.current = visibleArticles
    setRenderedCount(FIRST_CHUNK)
  }
  useEffect(() => {
    if (renderedCount >= visibleArticles.length) return undefined
    const grow = () => setRenderedCount((c) => Math.min(c + GROW_BATCH, visibleArticles.length))
    const idleSupported = typeof window.requestIdleCallback === 'function'
    const id = idleSupported
      ? window.requestIdleCallback(grow, { timeout: 200 })
      : window.setTimeout(grow, 16)
    return () => {
      if (idleSupported) window.cancelIdleCallback(id)
      else window.clearTimeout(id)
    }
  }, [renderedCount, visibleArticles.length])
  // Digest + trending are in the user's UI language, not the article-
  // filter language. The article language filter narrows the list,
  // but the AI surfaces are always meant to be read/scanned in the
  // user's native tongue.
  // Digest category follows the page mode: /tech surfaces the
  // tech-only digest, every other mode shows the general one.
  const digestCategory = mode === 'tech' ? 'tech' : 'all'
  const digestHook = useTodayDigest(locale, 'daily', digestCategory)
  const weeklyDigestHook = useTodayDigest(locale, 'weekly', digestCategory)

  // Manual regenerate handler shared by both digest cards. Fires
  // news-digest with force=true for the requested kind, then asks
  // the relevant hook to re-pull from DB so the UI shows the
  // freshly-generated text. Errors swallow silently — the spinner
  // stops via the card's internal state.
  const handleDigestRefresh = useCallback(async (kind) => {
    try {
      const { error } = await supabase.functions.invoke('news-digest', {
        body: {
          force: true,
          kind: kind === 'weekly' ? 'weekly' : 'daily',
          // Refresh hits the same digest bucket the user is viewing —
          // tech digest gets regenerated when triggered from /tech.
          category: digestCategory,
        },
      })
      if (error) {
        console.warn('[news-digest refresh]', error)
        return
      }
      if (kind === 'weekly') await weeklyDigestHook.refetch()
      else await digestHook.refetch()
    } catch (e) {
      console.warn('[news-digest refresh]', e)
    }
  }, [digestHook, weeklyDigestHook, digestCategory])
  // Trending category mirrors the digest: /tech reads the tech-scoped
  // row, every other mode reads the general one.
  const trendingHook = useTodayTrending(locale, digestCategory)
  // Pick the active-topic label for the inline summary line (below
  // the view chips). Trending list has both the label and the
  // keyword; the URL stores the keyword, so we map back here.
  const activeTopicLabel = useMemo(() => {
    if (!topic) return null
    const match = trendingHook.topics.find((t) => t.keyword === topic)
    return match?.label || topic
  }, [topic, trendingHook.topics])

  // Filters dropdown is closed by default — the View chips above are
  // the day-to-day knob. The dropdown opens on tap, contains the
  // Source / Language / Category chips, and auto-closes once the
  // user taps outside or makes a selection.
  // Only count the language as an active filter when it differs from
  // the implicit default (UI locale). The default state shouldn't
  // light up the "1 filter active" badge — that would imply the user
  // narrowed something when they didn't.
  // Default is now 'all'; "explicit" means the user pinned a single
  // language ('ar' or 'en') rather than leaving the chip on All.
  const langIsExplicit = !!langParam && langParam !== 'all'
  const activeFilterCount = (langIsExplicit ? 1 : 0) + (category ? 1 : 0) + (sourceKind ? 1 : 0) + (search ? 1 : 0) + (subId ? 1 : 0)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [subsSheetOpen, setSubsSheetOpen] = useState(false)
  const filterTriggerRef = useRef(null)
  // Anchor coords for the portal-rendered dropdown. Measured once on
  // open and pinned for the lifetime of the popover — re-measuring
  // on scroll/resize while open would be churn the user isn't asking
  // for since the popover auto-closes on those events anyway.
  const [filterAnchor, setFilterAnchor] = useState(null)

  const openFilters = useCallback(() => {
    if (filterTriggerRef.current) {
      setFilterAnchor(filterTriggerRef.current.getBoundingClientRect())
    }
    setFiltersOpen(true)
  }, [])

  // Close on Escape, viewport resize, and outside scroll (matching
  // the platform expectation for transient popovers).
  useEffect(() => {
    if (!filtersOpen) return undefined
    const close = () => setFiltersOpen(false)
    const onKey = (e) => { if (e.key === 'Escape') close() }
    window.addEventListener('keydown', onKey)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', close)
    }
  }, [filtersOpen])

  // Active-filter summary (the small chips below the view row). One
  // entry per non-null filter; tapping the ✕ clears it without
  // reopening the dropdown.
  const activeFilterLabels = useMemo(() => {
    const arr = []
    if (search) arr.push({ key: 'q', label: `"${search}"`, clear: () => { setSearch(null); setSearchDraft('') } })
    if (sourceKind) arr.push({ key: 'src', label: t(`news.sourceKind.${sourceKind}`), clear: () => setSourceKind(null) })
    if (activeSubName) arr.push({ key: 'sub', label: activeSubName, clear: () => setSubId(null) })
    if (langIsExplicit) arr.push({
      key: 'lang',
      label: t(`news.lang.${activeLangValue}`),
      clear: () => setLanguage(null),
    })
    if (category) arr.push({ key: 'cat', label: t(`news.category.${category}`), clear: () => setCategory(null) })
    if (activeTopicLabel) arr.push({ key: 'topic', label: activeTopicLabel, clear: () => setTopic(null) })
    return arr
  }, [search, sourceKind, activeSubName, langIsExplicit, activeLangValue, category, activeTopicLabel, setSearch, setSourceKind, setSubId, setLanguage, setCategory, setTopic, t])

  // Scroll memory. Save on every scroll (rAF-throttled) and on
  // unmount; restore once the article list has finished loading so
  // there's actually enough content to scroll into.
  const rootRef = useRef(null)
  const restoredRef = useRef(false)
  // Infinite-scroll sentinel — see the observer effect further down.
  const loadMoreSentinelRef = useRef(null)

  // Persist the current news URL (path + search) so a tap on the
  // News tab from somewhere else can restore the user's filters.
  // Writes on every URL change inside news (including drill-in to
  // /:id, since the user's expectation is "tab brings me back where
  // I left"). The tab-tap handler in App.jsx reads this key.
  useEffect(() => {
    // Persist ONLY the list-page URL (not article-detail URLs).
    // Article URLs aren't useful as a "return-to" target — the
    // user expects to come back to the filtered list they were on
    // when they opened the article. The article-detail overlay sits
    // ON TOP of the list, so when /phoenix/news/:id is active the
    // user's last-list URL is whatever was persisted previously.
    // Distinct keys per route — smart-tap restore shouldn't cross
    // between News / Social / Tech.
    if (location.pathname !== '/news'
        && location.pathname !== '/social'
        && location.pathname !== '/tech') return
    const storageKey = location.pathname === '/social' ? 'phoenix:social-last-url'
      : location.pathname === '/tech' ? 'phoenix:tech-last-url'
      : 'phoenix:news-last-url'
    try {
      sessionStorage.setItem(
        storageKey,
        `${location.pathname}${location.search}`,
      )
    } catch { /* private mode — ignore */ }
  }, [location.pathname, location.search])

  // Scroll-to-top on News-tab tap when already inside news. The
  // dispatch in App.jsx's bottom-bar handler routes here.
  useEffect(() => {
    const handler = () => {
      const scroller = findScrollableAncestor(rootRef.current)
      if (scroller) scroller.scrollTo({ top: 0, behavior: 'smooth' })
    }
    window.addEventListener('news:scroll-to-top', handler)
    return () => window.removeEventListener('news:scroll-to-top', handler)
  }, [])
  // Caps how many auto-pages we'll request while trying to reach a
  // saved scroll position. 5 extra pages × 30 rows = 150 articles,
  // way past what a casual scroller covers in one session.
  const restoreLoadMoreRef = useRef(0)

  useEffect(() => {
    const scroller = findScrollableAncestor(rootRef.current)
    if (!scroller) return undefined
    let raf = 0
    const save = () => {
      // Skip when the scroller has no real overflow — happens
      // briefly during page transitions and would otherwise stamp
      // a clamped 0 over the user's actual scrollTop.
      if (scroller.scrollHeight <= scroller.clientHeight + 50) return
      try { sessionStorage.setItem(SCROLL_STORAGE_KEY, String(scroller.scrollTop)) }
      catch { /* private mode etc — ignore */ }
    }
    const onScroll = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(save)
    }
    scroller.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      // Don't call save() in cleanup. The new PageTransition swaps
      // the route's children in a single commit instead of waiting
      // 200ms with a blank wrapper, so by the time this cleanup
      // fires the article page's DOM is already in the scroller,
      // its scrollHeight has replaced ours, and the browser has
      // clamped scrollTop to 0 — saving here would overwrite the
      // last good value with 0. The rAF-throttled save during
      // scrolling already captured the user's position before they
      // tapped the article; that value stays in sessionStorage.
      cancelAnimationFrame(raf)
      scroller.removeEventListener('scroll', onScroll)
    }
  }, [])

  useEffect(() => {
    if (restoredRef.current) return
    // The page assembles in stages: articles, digest, trending. Each
    // pass makes it taller, and an early scrollTop set gets clamped
    // to the current (shorter) maxScroll, dumping the user near the
    // top. Wait until every height-influencing source has settled
    // before kicking off the restore.
    if (articlesHook.loading || digestHook.loading || trendingHook.loading) return

    const raw = (() => { try { return sessionStorage.getItem(SCROLL_STORAGE_KEY) } catch { return null } })()
    const saved = raw ? parseInt(raw, 10) : 0
    if (!Number.isFinite(saved) || saved <= 0) {
      restoredRef.current = true
      return
    }

    const scroller = findScrollableAncestor(rootRef.current)
    if (!scroller) return

    // If the saved scroll position is past the current page height
    // and we have more pages available, ask for the next batch and
    // wait for it. The effect's dep array includes articles.length
    // and the loadMore loading state, so we'll re-enter on the next
    // batch and re-evaluate. Capped to prevent paging-to-infinity
    // when the saved value is bogus.
    const max = scroller.scrollHeight - scroller.clientHeight
    if (
      saved > max + 50
      && articlesHook.hasMore
      && !articlesHook.loadingMore
      && restoreLoadMoreRef.current < 5
    ) {
      restoreLoadMoreRef.current += 1
      articlesHook.loadMore?.()
      return
    }

    // Retry every animation frame for ~750ms. Each frame nudges the
    // scrollTop back to the target so late-arriving content (images,
    // fonts, lazy paints) that grows the page can't pull the user
    // away from where they were. Stop once we're within 4px or hit
    // the attempt cap.
    let attempts = 0
    const maxAttempts = 45
    const settle = () => {
      if (attempts++ >= maxAttempts) {
        restoredRef.current = true
        return
      }
      scroller.scrollTop = saved
      if (Math.abs(scroller.scrollTop - saved) <= 4) {
        restoredRef.current = true
        return
      }
      requestAnimationFrame(settle)
    }
    requestAnimationFrame(settle)
  // Re-run when any height-influencing surface settles AND when
  // articles.length grows (new page arrived) so we can keep paging
  // forward toward the saved position.
  }, [
    articlesHook.loading,
    articlesHook.loadingMore,
    articlesHook.loadMore,
    articlesHook.hasMore,
    articlesHook.articles.length,
    digestHook.loading,
    trendingHook.loading,
  ])

  // Infinite scroll. An IntersectionObserver on the sentinel fires
  // loadMore when the user gets within ~800px of it. We don't tear
  // down the observer between fetches — IntersectionObserver only
  // fires when the intersection state CHANGES, so back-to-back
  // loadMore calls only happen if the sentinel re-enters the viewport
  // after a new batch pushed it out. The loadMore() function itself
  // guards against in-flight duplicates.
  //
  // No-op when `hasMore` is false (we unmount the sentinel) and when
  // we're still on the first-page load.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined
    if (!articlesHook.hasMore || articlesHook.loading) return undefined
    const el = loadMoreSentinelRef.current
    if (!el) return undefined
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            articlesHook.loadMore?.()
          }
        }
      },
      { root: null, rootMargin: '800px 0px', threshold: 0 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [
    articlesHook.hasMore,
    articlesHook.loading,
    articlesHook.loadMore,
    // Re-attach the observer after every new batch — the sentinel
    // node may have been re-rendered with a new DOM identity.
    articlesHook.articles.length,
  ])

  const handleRefresh = useCallback(
    () => performRefresh([sourcesHook.refetch, articlesHook.refetch, savedHook.refetch, readHook.refetch, digestHook.refetch, weeklyDigestHook.refetch, trendingHook.refetch]),
    [sourcesHook.refetch, articlesHook.refetch, savedHook.refetch, readHook.refetch, digestHook.refetch, weeklyDigestHook.refetch, trendingHook.refetch],
  )

  // "Fetch now" button — manually triggers the fetch-news edge
  // function instead of waiting for the next 4h cron tick. Disabled
  // while in-flight (single call at a time). Shows an inline toast
  // with the inserted-article count when done.
  const [fetchingNow, setFetchingNow] = useState(false)
  const [fetchToast, setFetchToast] = useState(null)
  useEffect(() => {
    if (!fetchToast) return undefined
    const id = setTimeout(() => setFetchToast(null), 4000)
    return () => clearTimeout(id)
  }, [fetchToast])

  const handleFetchNow = useCallback(async () => {
    if (fetchingNow) return
    setFetchingNow(true)
    setFetchToast(null)
    try {
      const { data, error } = await supabase.functions.invoke('fetch-news', { body: {} })
      if (error) throw error
      const inserted = Number(data?.inserted) || 0
      setFetchToast({ kind: 'ok', count: inserted })
      // Pull the freshly-ingested rows + recount the source health
      // so the list reflects the new state immediately. Digest /
      // trending only update on their own crons so we don't refetch
      // them here.
      await Promise.all([
        sourcesHook.refetch?.(),
        articlesHook.refetch?.(),
      ])
    } catch (e) {
      setFetchToast({ kind: 'error', message: e?.message || '' })
    } finally {
      setFetchingNow(false)
    }
  }, [fetchingNow, sourcesHook, articlesHook])

  const lastFetchedAt = useMemo(() => {
    const dates = sourcesHook.sources
      .map((s) => s.last_fetched_at)
      .filter(Boolean)
      .sort()
    return dates.length > 0 ? dates[dates.length - 1] : null
  }, [sourcesHook.sources])

  return (
    <>
    <PullToRefresh onRefresh={handleRefresh}>
      <div
        ref={rootRef}
        className="space-y-3 content-fade pb-8"
        // Force the list onto its own GPU compositor layer so iOS
        // WebKit keeps the decoded card thumbnails in memory while
        // the article modal sits on top. Without this hint mobile
        // Safari was discarding the image bitmaps for the hidden
        // layer to reclaim memory, which is what looked like the
        // "posts re-render with image flash" on modal close.
        style={{ transform: 'translateZ(0)' }}
      >
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            onClick={goBack}
            aria-label={t('settings.back')}
            className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
          >
            <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-foreground flex items-center gap-2" dir="auto">
              {(() => {
                // Dynamic header: icon + title both come from `mode`
                // so /news, /social, /tech each show their own
                // identity. Easy to extend when we add more modes.
                const Icon = mode === 'social' ? Users
                  : mode === 'tech' ? Cpu
                  : Newspaper
                return <Icon className="w-5 h-5 text-app-primary-500" />
              })()}
              {t(mode === 'social' ? 'social.title'
                : mode === 'tech' ? 'tech.title'
                : 'news.title')}
            </h1>
            {lastFetchedAt && (
              <p className="text-[11px] text-muted-foreground mt-0.5" dir="auto">
                {t('news.lastFetched', { time: timeAgo(lastFetchedAt, locale) })}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={handleFetchNow}
            disabled={fetchingNow}
            aria-label={t('news.fetchNow.label')}
            className={cn(
              'p-1.5 rounded-md transition-colors',
              fetchingNow
                ? 'text-app-primary-500 cursor-wait'
                : 'text-muted-foreground hover:text-app-primary-500 hover:bg-accent',
            )}
          >
            {fetchingNow
              ? <Loader2 className="w-5 h-5 animate-spin" />
              : <RefreshCcw className="w-5 h-5" />}
          </button>
          {ownerView && (
            <button
              type="button"
              onClick={() => navigate('/news/health')}
              aria-label={t('news.health.title')}
              className="p-1.5 rounded-md text-muted-foreground hover:text-app-primary-500 hover:bg-accent transition-colors"
            >
              <Activity className="w-5 h-5" />
            </button>
          )}
        </div>

        {fetchToast && (
          <div
            className={cn(
              'rounded-md border px-3 py-2 text-xs flex items-center gap-2',
              fetchToast.kind === 'ok'
                ? 'border-app-primary-500/30 bg-app-primary-500/5 text-app-primary-500'
                : 'border-red-500/30 bg-red-500/5 text-red-500',
            )}
            dir="auto"
            role="status"
          >
            {fetchToast.kind === 'ok'
              ? (fetchToast.count > 0
                  ? t('news.fetchNow.success', { count: fetchToast.count })
                  : t('news.fetchNow.successEmpty'))
              : t('news.fetchNow.error')}
          </div>
        )}

        {/* Primary row: view chips (All / Read / Saved) + a single
            disclosure button on the right. "All" now excludes
            already-read articles (the main feed is the inbox);
            "Read" is the new dedicated archive. The button opens a
            popover with the Language / Category chips; the small
            summary line below shows what's currently filtered
            without forcing the user to re-open the popover. */}
        <div className="flex items-center gap-2">
          <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1 flex-1 min-w-0">
            {VIEW_CHIPS.map((v) => {
              const Icon = v.icon
              return (
                <FilterChip
                  key={v.key}
                  active={view === v.key}
                  onClick={() => setView(v.key)}
                  aria-label={t(`news.view.${v.key}`)}
                  title={t(`news.view.${v.key}`)}
                >
                  <Icon className="w-4 h-4" />
                  {v.key === 'subs' && subsUnreadCount > 0 && (
                    <span className="ms-1 font-mono text-[10px] opacity-80" dir="ltr">
                      {subsUnreadCount}
                    </span>
                  )}
                </FilterChip>
              )
            })}
          </div>
          <button
            type="button"
            onClick={() => setSubsSheetOpen(true)}
            aria-label={t('newsSubs.title')}
            title={t('newsSubs.title')}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs border border-input text-muted-foreground hover:bg-accent flex-shrink-0"
          >
            <Bell className="w-3.5 h-3.5" />
          </button>
          <button
            ref={filterTriggerRef}
            type="button"
            onClick={() => (filtersOpen ? setFiltersOpen(false) : openFilters())}
            aria-haspopup="dialog"
            aria-expanded={filtersOpen}
            aria-label={t('news.filters.toggle')}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs border transition-colors flex-shrink-0',
              filtersOpen || activeFilterCount > 0
                ? 'border-app-primary-500 bg-app-primary-500/10 text-app-primary-500'
                : 'border-input text-muted-foreground hover:bg-accent',
            )}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            {activeFilterCount > 0 && (
              <span className="font-mono leading-none" dir="ltr">{activeFilterCount}</span>
            )}
          </button>
        </div>

        {/* Active-filter summary. Quiet by design — tiny muted text
            with an inline ✕ that lets the user drop a filter without
            re-opening the popover. Hidden entirely when nothing is
            active so the row collapses to zero height. */}
        {activeFilterLabels.length > 0 && (
          <div className="flex items-center flex-wrap gap-x-2 gap-y-1 text-[11px] text-muted-foreground" dir="auto">
            {activeFilterLabels.map((f, i) => (
              <React.Fragment key={f.key}>
                {i > 0 && <span aria-hidden="true">·</span>}
                <span className="inline-flex items-center gap-1">
                  <span>{f.label}</span>
                  <button
                    type="button"
                    onClick={f.clear}
                    aria-label={t('common.clear')}
                    className="rounded-full p-0.5 -m-0.5 hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              </React.Fragment>
            ))}
          </div>
        )}
        {/* When a single source is filtered, surface a "View source page"
            shortcut so the user can subscribe / get notifications. */}
        {subId && (() => {
          const subSrc = sourcesHook.sources.find((s) => s.id === subId)
          if (!subSrc?.slug) return null
          return (
            <RouterLink
              to={`/news/source/${subSrc.slug}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-app-primary-500/10 text-app-primary-500 text-xs font-medium hover:bg-app-primary-500/15 w-fit"
              dir="auto"
            >
              <Bell className="w-3.5 h-3.5" />
              <span>{t('news.openSourcePage', { name: subSrc.name })}</span>
            </RouterLink>
          )
        })()}

        {/* Migration banner — only when we genuinely couldn't load
            any sources AND the per-fetch tableMissing flag is set.
            Previously the banner could surface on filtered Read /
            Saved views that legitimately had zero rows; if the
            sources fetch ever succeeded (chips rendering), the
            news_* tables clearly exist and the banner is a stale
            artefact of an earlier transient error. */}
        {(articlesHook.tableMissing || sourcesHook.tableMissing) && sourcesHook.sources.length === 0 && (
          <Card>
            <CardContent className="p-3 text-xs text-amber-700 dark:text-amber-400" dir="auto">
              {t('news.tableMissing')}
            </CardContent>
          </Card>
        )}

        {articlesHook.error && (
          <Card>
            <CardContent className="p-3 text-xs text-red-600" dir="auto">
              {articlesHook.error}
            </CardContent>
          </Card>
        )}

        {/* AI digest cards. Daily first when available; weekly sits
            underneath (and stays around the full week). Skipped on
            /social — owner wants the social timeline scannable. On
            /tech the digestCategory is 'tech', so these cards show
            the dedicated tech rundown. */}
        {view === 'all' && mode !== 'social' && digestHook.digest && (
          <DigestCard digest={digestHook.digest} locale={locale} t={t} onRefresh={handleDigestRefresh} />
        )}
        {view === 'all' && mode !== 'social' && weeklyDigestHook.digest && (
          <DigestCard digest={weeklyDigestHook.digest} locale={locale} t={t} onRefresh={handleDigestRefresh} />
        )}

        {/* Trending topics. Same All-view-only gate as the digest.
            Hidden on /social. On /news the chips come from the general
            news_trending_topics row; on /tech they come from the
            category='tech' row populated by news-trending-tech-daily,
            so the chips actually reflect tech headlines. */}
        {view === 'all' && (mode === 'news' || mode === 'tech') && (
          <TrendingChips
            topics={trendingHook.topics}
            activeKeyword={topic}
            onPick={setTopic}
            t={t}
          />
        )}

        {/* Recent + live football fixtures. Reuses the sports module's
            cached data — favourite teams float to the front. Hidden
            on /social and /tech (football fixtures are off-topic for
            a tech feed) and outside the "all" view. */}
        {view === 'all' && mode === 'news' && <RecentMatchesWidget />}

        {/* Reddit-only New / Top sort tabs. Show when the user scoped
            the source kind to Reddit. We expose two tabs rather than
            Reddit's four (hot / new / top / rising) because the cache
            only supports time-ordered and score-ordered views — hot
            and rising need Reddit's proprietary decay function, which
            we'd have to recompute server-side. */}
        {sourceKind === 'reddit' && (
          <div className="flex gap-1 p-1 bg-muted/60 rounded-full">
            <button
              type="button"
              onClick={() => setRedditSort(null)}
              className={cn(
                'flex-1 h-9 rounded-full text-xs font-semibold transition-colors',
                redditSort !== 'top'
                  ? 'bg-app-primary-500 text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              dir="auto"
            >
              {t('news.reddit.sortNew')}
            </button>
            <button
              type="button"
              onClick={() => setRedditSort('top')}
              className={cn(
                'flex-1 h-9 rounded-full text-xs font-semibold transition-colors',
                redditSort === 'top'
                  ? 'bg-app-primary-500 text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              dir="auto"
            >
              {t('news.reddit.sortTop')}
            </button>
          </div>
        )}

        {articlesHook.loading ? (
          <div className="space-y-2">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : visibleArticles.length === 0 ? (
          <EmptyState
            icon={view === 'saved' ? Bookmark : Globe}
            title={t(
              view === 'saved' ? 'news.savedEmpty'
              : view === 'read' ? 'news.readEmpty'
              : 'news.empty',
            )}
            description={t(
              view === 'saved' ? 'news.savedEmptyHint'
              : view === 'read' ? 'news.readEmptyHint'
              : 'news.emptyHint',
            )}
          />
        ) : (
          <>
            <div className="space-y-2">
              {visibleArticles.slice(0, renderedCount).map((a) => (
                a.source?.kind === 'reddit' ? (
                  <RedditCard
                    key={a.id}
                    article={a}
                    locale={locale}
                    isSaved={savedHook.savedIds.has(a.id)}
                    isRead={readHook.readIds.has(a.id)}
                    onToggleRead={readHook.toggleRead}
                    onAutoRead={handleAutoRead}
                    view={view}
                    from={mode}
                    onOpenArticle={openArticle}
                  />
                ) : (
                  <ArticleCard
                    key={a.id}
                    article={a}
                    locale={locale}
                    isSaved={savedHook.savedIds.has(a.id)}
                    isRead={readHook.readIds.has(a.id)}
                    onToggleRead={readHook.toggleRead}
                    onAutoRead={handleAutoRead}
                    view={view}
                    from={mode}
                    onOpenArticle={openArticle}
                  />
                )
              ))}
              {/* Spacer for the cards we haven't rendered yet, so the
                  scrollbar geometry + scroll-restore math see the
                  full-list height from the first frame. Cards stream
                  in via the requestIdleCallback loop above; this
                  placeholder shrinks as `renderedCount` grows and
                  disappears once everything is in the DOM. 160px is
                  an average per-card height across ArticleCard +
                  RedditCard for the bookkeeping. */}
              {renderedCount < visibleArticles.length && (
                <div
                  aria-hidden="true"
                  style={{ height: `${(visibleArticles.length - renderedCount) * 160}px` }}
                />
              )}
            </div>
            {/* Infinite-scroll sentinel + load-more fallback. The
                sentinel is an empty div that an IntersectionObserver
                (effect below) watches; when it enters the viewport
                (with an 800px rootMargin so we preload a screenful
                ahead), the observer fires loadMore. The button stays
                mounted as a fallback for environments where the
                observer doesn't fire (keyboard nav, accessibility
                tools) and as a visible loading indicator. */}
            {articlesHook.hasMore ? (
              <>
                <div ref={loadMoreSentinelRef} aria-hidden="true" className="h-1 w-full" />
                <div className="pt-2 flex justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={articlesHook.loadMore}
                    disabled={articlesHook.loadingMore}
                  >
                    {articlesHook.loadingMore && (
                      <Loader2 className="w-4 h-4 me-1.5 animate-spin" />
                    )}
                    {t('news.loadMore')}
                  </Button>
                </div>
              </>
            ) : (
              <p className="pt-2 text-[11px] text-center text-muted-foreground" dir="auto">
                {t('news.allLoaded')}
              </p>
            )}
          </>
        )}

        {/* Reddit external search affordance. Sits below the cached
            results when the user has a committed query AND scoped to
            Reddit. One tap fires the reddit-search edge function and
            renders the live hits inline tagged "From Reddit". */}
        {sourceKind === 'reddit' && search && (
          <div className="pt-2 space-y-2">
            {redditExternal.articles.length === 0 && !redditExternal.loading && (
              <button
                type="button"
                onClick={runRedditExternal}
                className="w-full h-10 inline-flex items-center justify-center gap-1.5 rounded-lg border border-input bg-background hover:bg-accent text-foreground text-xs font-semibold"
                dir="auto"
              >
                <Search className="w-4 h-4 text-orange-500" />
                {t('news.reddit.searchOnReddit')}
              </button>
            )}
            {redditExternal.loading && (
              <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5 px-3 py-2" dir="auto">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {t('news.reddit.searching')}
              </div>
            )}
            {redditExternal.error && (
              <Card>
                <CardContent className="p-3 text-xs text-red-600" dir="auto">
                  {t('news.reddit.searchError', { error: redditExternal.error })}
                </CardContent>
              </Card>
            )}
            {redditExternal.articles.length > 0 && (
              <>
                <div className="flex items-center justify-between pt-1">
                  <h2 className="text-xs font-semibold text-muted-foreground inline-flex items-center gap-1.5" dir="auto">
                    <Search className="w-3.5 h-3.5 text-orange-500" />
                    {t('news.reddit.externalResults')}
                  </h2>
                  <button
                    type="button"
                    onClick={redditExternal.reset}
                    className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                    dir="auto"
                  >
                    <X className="w-3 h-3" />
                    {t('news.reddit.clearExternal')}
                  </button>
                </div>
                <div className="space-y-2">
                  {redditExternal.articles.map((a) => (
                    <RedditCard
                      key={a.api_id || a.link}
                      article={{
                        ...a,
                        id: a.api_id || a.link,
                        source: {
                          name: a.subreddit_prefixed || (a.subreddit ? `r/${a.subreddit}` : 'Reddit'),
                          kind: 'reddit',
                          avatar_url: null,
                          subscriber_count: null,
                        },
                      }}
                      locale={locale}
                      isSaved={false}
                      isRead={false}
                      onToggleRead={null}
                      view="all"
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Filters popover. Rendered through a portal so it overlays
          everything (including the bottom tab bar) and isn't clipped
          by the scroll container. The transparent backdrop catches
          taps outside the popover for dismissal — same pattern as
          ProfileDropdown. */}
      {filtersOpen && createPortal(
        <>
          <div
            className="fixed inset-0 z-[60]"
            onClick={() => setFiltersOpen(false)}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-label={t('news.filters.title')}
            className={cn(
              'fixed z-[70] inset-x-3 sm:inset-x-auto sm:w-80 rounded-xl p-3 space-y-3 max-h-[70vh] overflow-y-auto',
              'bg-card/95 backdrop-blur-xl saturate-150 border border-border shadow-2xl',
              'animate-in fade-in slide-in-from-top-2',
            )}
            style={{
              top: (filterAnchor?.bottom ?? 0) + 8,
              // Pin to the side the trigger sits on so the popover
              // never tracks across the viewport on a wide screen.
              ...(filterAnchor
                ? (i18n.dir() === 'rtl'
                    ? { left: 'max(0.75rem, ' + filterAnchor.left + 'px)' }
                    : { right: 'max(0.75rem, ' + (window.innerWidth - filterAnchor.right) + 'px)' })
                : {}),
            }}
          >
            <div className="space-y-1.5">
              <p
                className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground px-1"
                dir="auto"
              >
                {t('news.search.label')}
              </p>
              <div className="relative">
                <Search className="absolute start-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                  type="search"
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  placeholder={t('news.search.placeholder')}
                  aria-label={t('news.search.label')}
                  className="w-full ps-9 pe-9 py-2 text-sm rounded-md bg-background border border-input focus:border-app-primary-500 focus:outline-none"
                />
                {searchDraft && (
                  <button
                    type="button"
                    onClick={() => setSearchDraft('')}
                    aria-label={t('common.clear')}
                    className="absolute end-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <p
                className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground px-1"
                dir="auto"
              >
                {t('news.sourceKind.label')}
              </p>
              <div className="flex gap-2 flex-wrap">
                {visibleSourceKinds.map((s) => (
                  <FilterChip
                    key={s.key}
                    active={sourceKind === s.value}
                    onClick={() => setSourceKind(s.value)}
                  >
                    {t(`news.sourceKind.${s.key}`)}
                  </FilterChip>
                ))}
              </div>
            </div>

            {subSources.length > 1 && (
              <div className="space-y-1.5">
                <p
                  className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground px-1"
                  dir="auto"
                >
                  {t('news.subSource.label')}
                </p>
                <div className="flex gap-2 flex-wrap">
                  <FilterChip
                    active={!subId}
                    onClick={() => setSubId(null)}
                  >
                    {t('news.subSource.all')}
                  </FilterChip>
                  {subSources.map((s) => (
                    <FilterChip
                      key={s.id}
                      active={subId === s.id}
                      onClick={() => setSubId(s.id)}
                    >
                      {/* Avatar + subscriber count for Reddit/YouTube
                          sub-sources gives the chip a "real channel"
                          feel rather than a plain text token. */}
                      {s.avatar_url && (
                        <img
                          src={s.avatar_url.replace(/&amp;/g, '&')}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          referrerPolicy="no-referrer"
                          className="w-4 h-4 rounded-full object-cover -ms-1 me-1 inline-block align-middle bg-background"
                        />
                      )}
                      <span className="align-middle">{s.name}</span>
                      {sourceKind === 'reddit' && typeof s.subscriber_count === 'number' && (
                        <span className="ms-1 text-[10px] opacity-70 font-mono align-middle" dir="ltr">
                          {compactNumber(s.subscriber_count)}
                        </span>
                      )}
                    </FilterChip>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <p
                className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground px-1"
                dir="auto"
              >
                {t('news.lang.label')}
              </p>
              <div className="flex gap-2 flex-wrap">
                {LANGUAGES.map((l) => (
                  <FilterChip
                    key={l.key}
                    active={activeLangValue === l.value}
                    onClick={() => setLanguage(l.value)}
                  >
                    {t(`news.lang.${l.key}`)}
                  </FilterChip>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <p
                className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground px-1"
                dir="auto"
              >
                {t('news.category.label')}
              </p>
              <div className="flex gap-2 flex-wrap">
                {CATEGORIES.map((c) => (
                  <FilterChip
                    key={c.key}
                    active={category === c.value}
                    onClick={() => setCategory(c.value)}
                  >
                    {t(`news.category.${c.key}`)}
                  </FilterChip>
                ))}
              </div>
            </div>
          </div>
        </>,
        document.body,
      )}
    </PullToRefresh>
    <NewsSubscriptionsSheet open={subsSheetOpen} onClose={() => setSubsSheetOpen(false)} />
    {/* Article modal. Rendered over the still-mounted list so the
        list DOM (and its decoded thumbnails) survive the round trip
        — that's what eliminates the image-decode flash users were
        reading as "freeze". z-20 sits below the bottom tab bar
        (z-30) so the nav stays operational; safe-area padding
        matches App.jsx's mobile shell so the back-arrow row lines
        up with the breadcrumb. overscroll-contain prevents iOS
        rubber-band from leaking into the list's PullToRefresh. */}
    {activeArticleId && (
      <div
        className="fixed inset-0 z-20 bg-background overflow-y-auto overscroll-contain"
        style={{
          paddingTop: 'calc(env(safe-area-inset-top) + 4rem)',
          paddingBottom: 'calc(72px + env(safe-area-inset-bottom))',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div className="max-w-screen-sm mx-auto px-3 py-3 sm:px-4 sm:py-4">
          <NewsArticlePage
            articleId={activeArticleId}
            onClose={closeArticle}
            onOpenArticle={openArticle}
          />
        </div>
      </div>
    )}
    </>
  )
}
